import { useEffect, useState } from 'react';
import { CreditCard, Loader2, Lock, CheckCircle2 } from 'lucide-react';
import { updateStorePaymentMethods, getStorePaymentMethodsAdmin, updateStoreTranzilaConfig } from '../hooks/useStoreApi';

// Payment Methods Settings Tab
// Props: storeId (string/number), onRegisterRefresh (function), chatId
export const SettingsTab = ({ storeId, onRegisterRefresh, chatId }) => {
    const CREDIT_CARD = 'אשראי';
    const NON_CC_METHODS = ['Bit', 'PayBox', 'מזומן', 'העברה בנקאית'];
    const [enabledMethods, setEnabledMethods] = useState(NON_CC_METHODS);
    const [originalMethods, setOriginalMethods] = useState(NON_CC_METHODS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Tranzila config (mirrors backend payment-methods GET response shape)
    const [tranzila, setTranzila] = useState({
        enabled: false,
        terminal_name: '',
        terminal_password_set: false,
        response_secret_set: false,
    });
    const [originalTranzila, setOriginalTranzila] = useState(tranzila);
    // Write-only credential drafts. Empty string means "no change"; leaving as
    // empty after the user typed something means "clear it" — see save handler.
    const [tranzilaDraft, setTranzilaDraft] = useState({
        terminalName: '',
        terminalPassword: '',
        responseSecret: '',
        clearPassword: false,
        clearResponseSecret: false,
    });
    const [savingTranzila, setSavingTranzila] = useState(false);
    const [tranzilaError, setTranzilaError] = useState(null);
    const [tranzilaSuccess, setTranzilaSuccess] = useState(null);

    const applyServerState = (data) => {
        const list = Array.isArray(data?.payment_methods) && data.payment_methods.length ? data.payment_methods : NON_CC_METHODS;
        setEnabledMethods(list);
        setOriginalMethods(list);
        const tz = data?.tranzila || { enabled: false, terminal_name: '', terminal_password_set: false, response_secret_set: false };
        setTranzila(tz);
        setOriginalTranzila(tz);
        setTranzilaDraft({ terminalName: tz.terminal_name || '', terminalPassword: '', responseSecret: '', clearPassword: false, clearResponseSecret: false });
    };

    useEffect(() => {
        let mounted = true;
        const fetchMethods = async () => {
            if (!storeId) return;
            setLoading(true);
            setError(null);
            try {
                const data = await getStorePaymentMethodsAdmin(storeId, chatId);
                if (mounted) applyServerState(data);
            } catch (e) {
                if (mounted) setError(e.message || 'שגיאה בטעינת שיטות תשלום');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchMethods();
        return () => { mounted = false; };
    }, [storeId]);

    useEffect(() => {
        if (onRegisterRefresh) {
            onRegisterRefresh('settings', async () => {
                try {
                    const data = await getStorePaymentMethodsAdmin(storeId, chatId);
                    applyServerState(data);
                } catch (_) { /* silent */ }
            });
        }
    }, [onRegisterRefresh, storeId]);

    const tranzilaFullyConfigured = tranzila.enabled
        && !!(tranzila.terminal_name || '').trim()
        && tranzila.terminal_password_set
        && tranzila.response_secret_set;

    const allMethods = [...NON_CC_METHODS, CREDIT_CARD];

    const toggleMethod = (method) => {
        if (method === CREDIT_CARD && !tranzilaFullyConfigured) {
            // Don't let the admin save an "אשראי" option until Tranzila is configured.
            setError('יש להגדיר חיבור Tranzila תקין לפני הפעלת תשלום בכרטיס אשראי');
            setTimeout(() => setError(null), 5000);
            return;
        }
        setEnabledMethods(prev => prev.includes(method) ? prev.filter(m => m !== method) : [...prev, method]);
    };

    const hasChanges = (() => {
        if (enabledMethods.length !== originalMethods.length) return true;
        const a = [...enabledMethods].sort();
        const b = [...originalMethods].sort();
        return a.some((v, i) => v !== b[i]);
    })();

    const handleSave = async () => {
        if (!storeId || saving || !hasChanges) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await updateStorePaymentMethods(storeId, enabledMethods, chatId);
            setOriginalMethods(enabledMethods);
            setSuccess('שיטות התשלום נשמרו בהצלחה');
            setTimeout(() => setSuccess(null), 4000);
        } catch (e) {
            setError(e.message || 'שמירת השינויים נכשלה');
        } finally {
            setSaving(false);
        }
    };

    const tranzilaHasChanges = (
        tranzilaDraft.terminalName !== (originalTranzila.terminal_name || '')
        || tranzila.enabled !== originalTranzila.enabled
        || tranzilaDraft.terminalPassword.length > 0
        || tranzilaDraft.responseSecret.length > 0
        || tranzilaDraft.clearPassword
        || tranzilaDraft.clearResponseSecret
    );

    const handleSaveTranzila = async () => {
        if (!storeId || savingTranzila || !tranzilaHasChanges) return;
        setSavingTranzila(true);
        setTranzilaError(null);
        setTranzilaSuccess(null);
        try {
            const payload = {
                enabled: tranzila.enabled,
                terminalName: tranzilaDraft.terminalName.trim(),
            };
            if (tranzilaDraft.clearPassword) {
                payload.terminalPassword = '';
            } else if (tranzilaDraft.terminalPassword) {
                payload.terminalPassword = tranzilaDraft.terminalPassword;
            }
            if (tranzilaDraft.clearResponseSecret) {
                payload.responseSecret = '';
            } else if (tranzilaDraft.responseSecret) {
                payload.responseSecret = tranzilaDraft.responseSecret;
            }
            await updateStoreTranzilaConfig(storeId, payload, chatId);
            // Reload to get authoritative state (presence booleans flip after save)
            const data = await getStorePaymentMethodsAdmin(storeId, chatId);
            applyServerState(data);
            setTranzilaSuccess('הגדרות Tranzila נשמרו בהצלחה');
            setTimeout(() => setTranzilaSuccess(null), 4000);
        } catch (e) {
            setTranzilaError(e.message || 'שמירת הגדרות Tranzila נכשלה');
        } finally {
            setSavingTranzila(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-card p-8 border border-mekomit-100">
                <h2 className="text-2xl font-bold text-mekomit-900 mb-2 flex items-center gap-2"><CreditCard className="w-6 h-6 text-mekomit-600" />הגדרות תשלום</h2>
                <p className="text-mekomit-700 text-sm mb-6">בחר אילו אמצעי תשלום יהיו זמינים ללקוחות בעת ההזמנה. תשלום בכרטיס אשראי דורש חיבור פעיל ל-Tranzila (ראה למטה).</p>
                {loading ? (
                    <div className="flex items-center gap-2 text-mekomit-600"><Loader2 className="w-5 h-5 animate-spin" /> טוען...</div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                        {allMethods.map(method => {
                            const active = enabledMethods.includes(method);
                            const isCC = method === CREDIT_CARD;
                            const disabled = isCC && !tranzilaFullyConfigured && !active;
                            return (
                                <button
                                    type="button"
                                    key={method}
                                    onClick={() => toggleMethod(method)}
                                    disabled={disabled}
                                    className={`group relative flex flex-col items-center justify-center gap-2 px-4 py-5 rounded-xl border-2 transition-all text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-mekomit-400 ${active ? 'border-mekomit-500 bg-gradient-to-br from-mekomit-50 to-mekomit-100 shadow-md' : 'border-mekomit-200 bg-white hover:border-mekomit-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    aria-pressed={active ? 'true' : 'false'}
                                    title={disabled ? 'יש להגדיר Tranzila לפני הפעלת כרטיס אשראי' : ''}
                                >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-mekomit-900 font-bold text-xs tracking-wide shadow-sm transition-colors ${active ? 'bg-mekomit-400/20 border border-mekomit-400' : 'bg-surface-muted border border-line-strong'} `}>{method}</div>
                                    <span className={`transition-colors ${active ? 'text-mekomit-800' : 'text-ink-muted'}`}>{active ? 'פעיל' : 'מושבת'}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
                {error && <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">{error}</div>}
                {success && <div className="mb-4 p-3 bg-success-50 border border-success-200 rounded-lg text-success-700 text-sm">{success}</div>}
                <div className="flex items-center justify-between border-t border-mekomit-100 pt-4 mt-2">
                    <div className="text-xs text-ink-muted">ברירת מחדל: כל השיטות זמינות אם לא נשמרו הגדרות</div>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                        className={`px-6 py-3 rounded-xl font-semibold text-sm transition-all ${!hasChanges || saving ? 'bg-surface-sunken text-ink-subtle cursor-not-allowed' : 'bg-gradient-to-r from-mekomit-500 to-mekomit-400 text-ink-strong hover:from-mekomit-600 hover:to-mekomit-500 shadow-md'}`}
                    >
                        {saving ? 'שומר...' : 'שמור שינויים'}
                    </button>
                </div>
            </div>

            {/* Tranzila configuration */}
            <div className="bg-white rounded-2xl shadow-card p-8 border border-mekomit-100">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-mekomit-900 flex items-center gap-2"><Lock className="w-5 h-5 text-mekomit-600" />חיבור Tranzila לסליקת אשראי</h3>
                    {tranzilaFullyConfigured && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-success-700 bg-success-50 border border-success-200 px-2 py-1 rounded-md">
                            <CheckCircle2 className="w-3.5 h-3.5" /> מחובר
                        </span>
                    )}
                </div>
                <p className="text-sm text-mekomit-700 mb-5">הזן את פרטי המסוף שקיבלת מ-Tranzila. הסיסמה והמפתח לאימות תגובה נשמרים מוצפנים ב-AWS SSM ולא נחשפים שוב אחרי השמירה.</p>

                <div className="space-y-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={tranzila.enabled}
                            onChange={(e) => setTranzila(t => ({ ...t, enabled: e.target.checked }))}
                            className="mt-1 w-4 h-4 text-mekomit-600 border-line-strong rounded focus:ring-2 focus:ring-mekomit-500"
                        />
                        <span className="flex-1">
                            <span className="block text-sm font-medium text-ink-strong">הפעל תשלום בכרטיס אשראי דרך Tranzila</span>
                            <span className="block text-xs text-ink-subtle mt-0.5">לקוחות יוכלו לבחור "אשראי" בקופה רק כאשר ההגדרה פעילה וכל הפרטים מולאו.</span>
                        </span>
                    </label>

                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">שם מסוף (Terminal Name / supplier)</label>
                        <input
                            type="text"
                            value={tranzilaDraft.terminalName}
                            onChange={(e) => setTranzilaDraft(d => ({ ...d, terminalName: e.target.value }))}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-mekomit-500 focus:border-transparent text-ink-strong"
                            placeholder="myterminal"
                            autoComplete="off"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">
                            סיסמת מסוף (TranzilaPW)
                            {tranzila.terminal_password_set && <span className="text-xs text-success-600 mr-2">•••• מוגדר</span>}
                        </label>
                        <input
                            type="password"
                            value={tranzilaDraft.clearPassword ? '' : tranzilaDraft.terminalPassword}
                            onChange={(e) => setTranzilaDraft(d => ({ ...d, terminalPassword: e.target.value, clearPassword: false }))}
                            disabled={tranzilaDraft.clearPassword}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-mekomit-500 focus:border-transparent text-ink-strong disabled:bg-surface-muted"
                            placeholder={tranzila.terminal_password_set ? 'השאר ריק כדי לא לשנות' : 'הזן את הסיסמה'}
                            autoComplete="new-password"
                        />
                        {tranzila.terminal_password_set && (
                            <label className="inline-flex items-center gap-2 mt-2 text-xs text-danger-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={tranzilaDraft.clearPassword}
                                    onChange={(e) => setTranzilaDraft(d => ({ ...d, clearPassword: e.target.checked, terminalPassword: e.target.checked ? '' : d.terminalPassword }))}
                                    className="w-3.5 h-3.5"
                                />
                                הסר את הסיסמה הקיימת
                            </label>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">
                            מפתח אימות תגובה (Response HMAC Secret)
                            {tranzila.response_secret_set && <span className="text-xs text-success-600 mr-2">•••• מוגדר</span>}
                        </label>
                        <input
                            type="password"
                            value={tranzilaDraft.clearResponseSecret ? '' : tranzilaDraft.responseSecret}
                            onChange={(e) => setTranzilaDraft(d => ({ ...d, responseSecret: e.target.value, clearResponseSecret: false }))}
                            disabled={tranzilaDraft.clearResponseSecret}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-mekomit-500 focus:border-transparent text-ink-strong disabled:bg-surface-muted"
                            placeholder={tranzila.response_secret_set ? 'השאר ריק כדי לא לשנות' : 'הזן את ה-secret'}
                            autoComplete="new-password"
                        />
                        {tranzila.response_secret_set && (
                            <label className="inline-flex items-center gap-2 mt-2 text-xs text-danger-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={tranzilaDraft.clearResponseSecret}
                                    onChange={(e) => setTranzilaDraft(d => ({ ...d, clearResponseSecret: e.target.checked, responseSecret: e.target.checked ? '' : d.responseSecret }))}
                                    className="w-3.5 h-3.5"
                                />
                                הסר את המפתח הקיים
                            </label>
                        )}
                        <p className="text-xs text-ink-subtle mt-1">המפתח (general secret או personal secret) משמש לאימות חתימת התגובות של Tranzila.</p>
                    </div>
                </div>

                {tranzilaError && <div className="mt-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">{tranzilaError}</div>}
                {tranzilaSuccess && <div className="mt-4 p-3 bg-success-50 border border-success-200 rounded-lg text-success-700 text-sm">{tranzilaSuccess}</div>}

                <div className="flex items-center justify-end pt-4 mt-4 border-t border-mekomit-100">
                    <button
                        type="button"
                        onClick={handleSaveTranzila}
                        disabled={!tranzilaHasChanges || savingTranzila}
                        className={`px-6 py-3 rounded-xl font-semibold text-sm transition-all ${!tranzilaHasChanges || savingTranzila ? 'bg-surface-sunken text-ink-subtle cursor-not-allowed' : 'bg-gradient-to-r from-mekomit-500 to-mekomit-400 text-ink-strong hover:from-mekomit-600 hover:to-mekomit-500 shadow-md'}`}
                    >
                        {savingTranzila ? 'שומר...' : 'שמור הגדרות Tranzila'}
                    </button>
                </div>
            </div>
        </div>
    );
};
